/**
 * Onboarding Trends Page
 *
 * Shows conversion rate trends over time and signup velocity.
 * Uses Recharts for multi-line charts and KPI cards for velocity stats.
 */

import { useState, useEffect } from 'react';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import KPICard from '../../../components/founder-dashboard/KPICard';
import TrendChart from '../../../components/founder-dashboard/TrendChart';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';
import {
  ArrowTrendingUpIcon,
  UserPlusIcon,
  CalendarDaysIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

// Cohort-based conversion trends. Each point is one weekly signup cohort;
// the y-values are "where are those users now" percentages. Keys must match
// the /onboarding/trends endpoint's `conversionTrends` shape.
const CONVERSION_LINES = [
  { key: 'pct_activated', name: '% Activated', color: '#22C55E' },
  { key: 'pct_stuck_signed_up', name: '% Stuck at Signed Up', color: '#EF4444' },
  { key: 'pct_stuck_searched', name: '% Stuck at Searched', color: '#F59E0B' },
];

// Least-squares linear regression over a series of { y } points indexed
// 0..n-1. Returns { slope, intercept } for y = slope*x + intercept.
// Returns null if the series has fewer than 2 valid points.
function linearRegression(values) {
  const points = values
    .map((v, i) => ({ x: i, y: typeof v === 'number' && !isNaN(v) ? v : null }))
    .filter((p) => p.y !== null);
  const n = points.length;
  if (n < 2) return null;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumX2 += p.x * p.x;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

// Takes the raw conversionTrends rows + the CONVERSION_LINES config, and
// returns both an augmented data array (with {key}_trend fields) and an
// expanded line list that includes dashed trend overlays.
function buildTrendlines(rows, lines) {
  if (rows.length < 2) return { augmentedRows: rows, allLines: lines };

  const trendByKey = {};
  const trendLines = [];
  for (const line of lines) {
    const reg = linearRegression(rows.map((r) => r[line.key]));
    if (!reg) continue;
    trendByKey[line.key] = reg;
    trendLines.push({
      key: `${line.key}_trend`,
      name: `${line.name} (trend)`,
      color: line.color,
      strokeDasharray: '4 4',
      strokeWidth: 1.5,
      isTrend: true,
      legendType: 'plainline',
    });
  }

  const augmentedRows = rows.map((row, i) => {
    const enriched = { ...row };
    for (const [key, reg] of Object.entries(trendByKey)) {
      enriched[`${key}_trend`] = parseFloat((reg.intercept + reg.slope * i).toFixed(1));
    }
    return enriched;
  });

  return { augmentedRows, allLines: [...lines, ...trendLines] };
}

export default function OnboardingTrendsPage() {
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingTrends();
      setData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[OnboardingTrends] Error fetching data:', err);
      setError(
        err.response?.data?.error || err.message || 'Failed to load data'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">
          Couldn't load onboarding trends
        </p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
        >
          Try Again
        </button>
      </div>
    );
  }

  const conversionTrends = data?.conversionTrends || [];
  const signupVelocity = data?.signupVelocity || [];
  const velocityKPIs = data?.velocityKPIs || {};

  // Build line configs for whatever keys exist in the data
  const availableLines = CONVERSION_LINES.filter(
    (line) =>
      conversionTrends.length > 0 && conversionTrends[0]?.[line.key] !== undefined
  );

  // Fallback: if API returns different keys, detect them
  const baseLines =
    availableLines.length > 0
      ? availableLines
      : conversionTrends.length > 0
        ? Object.keys(conversionTrends[0])
            .filter((k) => k !== 'date' && k !== 'week' && k !== 'cohort_size' && !k.endsWith('_trend'))
            .map((k, i) => ({
              key: k,
              name: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
              color: ['#3B82F6', '#22C55E', '#F59E0B', '#8B5CF6', '#EF4444'][i % 5],
            }))
        : [];

  // Compute linear-regression trendlines for each base metric and append them
  // as dashed overlays so direction (up/down) is readable at a glance.
  const { augmentedRows: trendData, allLines: detectedLines } = buildTrendlines(
    conversionTrends,
    baseLines
  );

  // Same trendline treatment for the Daily Signups area chart so signup
  // velocity and per-day activation yield are readable as up/down at a glance.
  const signupBaseLines = [
    { key: 'signups', name: 'New Signups', color: '#3B82F6' },
    ...(signupVelocity[0]?.activated !== undefined
      ? [{ key: 'activated', name: 'Activated', color: '#22C55E' }]
      : []),
  ];
  const {
    augmentedRows: signupData,
    allLines: signupLines,
  } = buildTrendlines(signupVelocity, signupBaseLines);

  return (
    <div className="space-y-6">
      {/* Refresh Indicator */}
      <div className="flex justify-end">
        <RefreshIndicator
          lastUpdated={lastUpdated}
          onRefresh={fetchData}
          loading={loading}
        />
      </div>

      {/* Signup Velocity KPIs */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Signup Velocity
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Signups This Week"
            value={velocityKPIs.thisWeek}
            change={velocityKPIs.weekOverWeekChange}
            icon={UserPlusIcon}
            loading={loading}
          />
          <KPICard
            title="Avg Daily Signups"
            value={velocityKPIs.avgDaily}
            format="decimal"
            icon={CalendarDaysIcon}
            loading={loading}
          />
          <KPICard
            title="Signups Last 30d"
            value={velocityKPIs.last30Days}
            icon={ChartBarIcon}
            loading={loading}
          />
          <KPICard
            title="Activation Rate"
            value={velocityKPIs.activationRate}
            format="percent"
            change={velocityKPIs.activationRateChange}
            icon={ArrowTrendingUpIcon}
            loading={loading}
          />
        </div>
      </div>

      {/* Signup Velocity Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Daily Signups (Last 30 Days)
        </h3>
        {loading ? (
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse" />
        ) : signupData.length > 0 ? (
          <TrendChart
            data={signupData}
            lines={signupLines}
            type="area"
            height={280}
            xAxisKey="date"
          />
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            No signup velocity data available yet.
          </div>
        )}
      </div>

      {/* Cohort Conversion Trends */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Cohort Conversion Trends
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Each point is one weekly signup cohort. Y-values show where those users
          are <em>right now</em>. Compare recent cohorts to older ones to see whether
          product changes (KYC, FRE) are moving users out of the early-stage leaks.
        </p>
        {loading ? (
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse" />
        ) : trendData.length > 0 && detectedLines.length > 0 ? (
          <TrendChart
            data={trendData}
            lines={detectedLines}
            type="line"
            height={300}
            xAxisKey="week"
          />
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <ArrowTrendingUpIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Cohort trends will appear as more snapshots accumulate.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
